import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Alert,
  Chip,
  IconButton,
  Tooltip,
  Paper,
  Stepper,
  Step,
  StepLabel,
  Divider,
  CircularProgress
} from "@mui/material";
import {
  Add,
  Receipt,
  PictureAsPdf,
  GetApp,
  Refresh,
  Close,
  CheckCircle,
  Cancel,
  Description
} from "@mui/icons-material";
import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL || "";

const getAuthHeaders = () => {
  const token = localStorage.getItem("token");
  return { Authorization: `Bearer ${token}` };
};

export default function Retenciones() {
  // Estados principales
  const [compras, setCompras] = useState([]);
  const [retenciones, setRetenciones] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [compraSeleccionada, setCompraSeleccionada] = useState(null);
  
  // Estados del formulario
  const [activeStep, setActiveStep] = useState(0);
  const [catalogosSRI, setCatalogosSRI] = useState({
    retencionesRenta: [],
    retencionesIVA: []
  });
  
  // Datos de la retención
  const [retencionData, setRetencionData] = useState({
    // Retención Renta
    aplicarRetencionRenta: false,
    codigoRetencionRenta: "",
    baseImponibleRenta: 0,
    porcentajeRetencionRenta: 0,
    valorRetencionRenta: 0,
    
    // Retención IVA
    aplicarRetencionIva: false,
    codigoRetencionIva: "",
    baseImponibleIva: 0,
    porcentajeRetencionIva: 0,
    valorRetencionIva: 0,
    
    // Datos del comprobante
    establecimiento: "001",
    puntoEmision: "001",
    secuencial: "",
    fechaEmision: new Date().toISOString().split("T")[0]
  });
  
  // Snackbar
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info"
  });

  const steps = ["Seleccionar Compra", "Configurar Retención", "Confirmar"];

  // Cargar datos iniciales
  useEffect(() => {
    cargarComprasSinRetencion();
    cargarCatalogosSRI();
  }, []);

  const cargarComprasSinRetencion = async () => {
    try {
      setLoading(true);
      const res = await axios.get(
        `${API_URL}/api/contabilidad/compras`,
        { headers: getAuthHeaders() }
      );
      
      // Mostrar TODAS las compras (temporalmente para debug)
      // Filtrar solo compras sin retención
      const todasCompras = res.data || [];
      const comprasSinRetencion = todasCompras.filter(c => !c.tiene_retencion);
      
      console.log("📊 Total compras:", todasCompras.length);
      console.log("✅ Compras sin retención:", comprasSinRetencion.length);
      console.log("📋 Compras:", todasCompras);
      
      setCompras(todasCompras); // Mostrar todas por ahora
      
      if (todasCompras.length === 0) {
        showSnackbar("No hay compras registradas. Registra una compra primero.", "info");
      }
    } catch (error) {
      console.error("❌ Error cargando compras:", error);
      showSnackbar("Error al cargar compras: " + (error.response?.data?.error || error.message), "error");
    } finally {
      setLoading(false);
    }
  };

  const cargarCatalogosSRI = async () => {
    try {
      const res = await axios.get(
        `${API_URL}/api/contabilidad/catalogos-sri`,
        { headers: getAuthHeaders() }
      );
      
      setCatalogosSRI(res.data.catalogos);
      console.log("✅ Catálogos SRI cargados");
    } catch (error) {
      console.error("❌ Error cargando catálogos SRI:", error);
    }
  };

  const showSnackbar = (message, severity = "info") => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const abrirDialogCreacion = async (compra) => {
    setCompraSeleccionada(compra);
    
    // Obtener próximo número de retención
    try {
      const res = await axios.get(
        `${API_URL}/api/contabilidad/retenciones/proximo-numero`,
        {
          headers: getAuthHeaders(),
          params: { puntoEmision: "001" }
        }
      );
      
      const subtotal = parseFloat(compra.totales?.subtotal15 || 0) + parseFloat(compra.totales?.subtotal0 || 0);
      const iva = parseFloat(compra.totales?.iva15 || 0);
      
      setRetencionData({
        ...retencionData,
        establecimiento: res.data.establecimiento,
        puntoEmision: res.data.puntoEmision,
        secuencial: res.data.secuencial.toString().padStart(9, "0"),
        baseImponibleRenta: parseFloat(subtotal.toFixed(2)),
        baseImponibleIva: parseFloat(iva.toFixed(2))
      });
      
      console.log("📄 Próximo número de retención:", res.data.numeroFormateado);
    } catch (error) {
      console.error("❌ Error obteniendo número de retención:", error);
      // Usar valores por defecto
      const subtotal = parseFloat(compra.totales?.subtotal15 || 0) + parseFloat(compra.totales?.subtotal0 || 0);
      const iva = parseFloat(compra.totales?.iva15 || 0);
      
      setRetencionData({
        ...retencionData,
        baseImponibleRenta: parseFloat(subtotal.toFixed(2)),
        baseImponibleIva: parseFloat(iva.toFixed(2)),
        secuencial: "000000001"
      });
    }
    
    setDialogOpen(true);
    setActiveStep(0);
  };

  const cerrarDialog = () => {
    setDialogOpen(false);
    setCompraSeleccionada(null);
    setActiveStep(0);
    setRetencionData({
      aplicarRetencionRenta: false,
      codigoRetencionRenta: "",
      baseImponibleRenta: 0,
      porcentajeRetencionRenta: 0,
      valorRetencionRenta: 0,
      aplicarRetencionIva: false,
      codigoRetencionIva: "",
      baseImponibleIva: 0,
      porcentajeRetencionIva: 0,
      valorRetencionIva: 0,
      establecimiento: "001",
      puntoEmision: "001",
      secuencial: "",
      fechaEmision: new Date().toISOString().split("T")[0]
    });
  };

  const handleChangeRetencionRenta = (field, value) => {
    const newData = { ...retencionData, [field]: value };
    
    // Si cambia el código, actualizar porcentaje
    if (field === "codigoRetencionRenta") {
      const retencion = catalogosSRI.retencionesRenta.find(r => (r.codigo || r.value) === value);
      if (retencion) {
        newData.porcentajeRetencionRenta = retencion.porcentaje;
      } else {
        // Fallback de porcentajes
        const fallback = { "303": 10, "304": 2, "307": 2, "312": 1, "332": 1, "344": 8 };
        newData.porcentajeRetencionRenta = fallback[value] || 1;
      }
      newData.valorRetencionRenta = parseFloat(((newData.baseImponibleRenta * newData.porcentajeRetencionRenta) / 100).toFixed(2));
    }
    
    // Si cambia la base imponible, recalcular valor
    if (field === "baseImponibleRenta") {
      newData.valorRetencionRenta = parseFloat(((value * newData.porcentajeRetencionRenta) / 100).toFixed(2));
    }
    
    setRetencionData(newData);
  };

  const handleChangeRetencionIva = (field, value) => {
    const newData = { ...retencionData, [field]: value };
    
    // Si cambia el código, actualizar porcentaje
    if (field === "codigoRetencionIva") {
      const retencion = catalogosSRI.retencionesIVA.find(r => (r.codigo || r.value) === value);
      if (retencion) {
        newData.porcentajeRetencionIva = retencion.porcentaje;
      } else {
        // Fallback de porcentajes
        const fallback = { "1": 10, "2": 20, "3": 30, "9": 70, "10": 100 };
        newData.porcentajeRetencionIva = fallback[value] || 30;
      }
      newData.valorRetencionIva = parseFloat(((newData.baseImponibleIva * newData.porcentajeRetencionIva) / 100).toFixed(2));
    }
    
    // Si cambia la base imponible, recalcular valor
    if (field === "baseImponibleIva") {
      newData.valorRetencionIva = parseFloat(((value * newData.porcentajeRetencionIva) / 100).toFixed(2));
    }
    
    setRetencionData(newData);
  };

  const handleNext = () => {
    if (activeStep === 0) {
      // Validar que haya compra seleccionada
      if (!compraSeleccionada) {
        showSnackbar("Debe seleccionar una compra", "warning");
        return;
      }
    } else if (activeStep === 1) {
      // Validar configuración de retención
      if (!retencionData.aplicarRetencionRenta && !retencionData.aplicarRetencionIva) {
        showSnackbar("Debe aplicar al menos una retención", "warning");
        return;
      }
      
      if (retencionData.aplicarRetencionRenta && !retencionData.codigoRetencionRenta) {
        showSnackbar("Seleccione un código de retención en la fuente", "warning");
        return;
      }
      
      if (retencionData.aplicarRetencionIva && !retencionData.codigoRetencionIva) {
        showSnackbar("Seleccione un código de retención de IVA", "warning");
        return;
      }
      
      console.log("✅ Retención configurada:", retencionData);
    }
    
    setActiveStep(activeStep + 1);
  };

  const handleBack = () => {
    setActiveStep(activeStep - 1);
  };

  const crearRetencion = async () => {
    try {
      setLoading(true);
      
      const payload = {
        compra_id: compraSeleccionada.id,
        aplicar_retencion_renta: retencionData.aplicarRetencionRenta,
        codigo_retencion_renta: retencionData.codigoRetencionRenta,
        base_imponible_renta: retencionData.baseImponibleRenta,
        porcentaje_retencion_renta: retencionData.porcentajeRetencionRenta,
        valor_retencion_renta: retencionData.valorRetencionRenta,
        aplicar_retencion_iva: retencionData.aplicarRetencionIva,
        codigo_retencion_iva: retencionData.codigoRetencionIva,
        base_imponible_iva: retencionData.baseImponibleIva,
        porcentaje_retencion_iva: retencionData.porcentajeRetencionIva,
        valor_retencion_iva: retencionData.valorRetencionIva,
        establecimiento: retencionData.establecimiento,
        punto_emision: retencionData.puntoEmision,
        secuencial: retencionData.secuencial,
        fecha_emision: retencionData.fechaEmision
      };
      
      const res = await axios.post(
        `${API_URL}/api/contabilidad/retenciones`,
        payload,
        { headers: getAuthHeaders() }
      );
      
      showSnackbar("Retención creada exitosamente", "success");
      cerrarDialog();
      cargarComprasSinRetencion();
      
      console.log("✅ Retención creada:", res.data);
    } catch (error) {
      console.error("❌ Error creando retención:", error);
      showSnackbar(
        error.response?.data?.error || "Error al crear retención",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("es-EC", {
      style: "currency",
      currency: "USD"
    }).format(value || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("es-EC");
  };

  const totalRetenido = () => {
    let total = 0;
    if (retencionData.aplicarRetencionRenta) {
      total += retencionData.valorRetencionRenta;
    }
    if (retencionData.aplicarRetencionIva) {
      total += retencionData.valorRetencionIva;
    }
    return total;
  };

  const descargarPDFRetencion = async (retencionId) => {
    if (!retencionId) {
      showSnackbar("No se encontró el ID de la retención", "error");
      return;
    }
    try {
      const response = await axios.get(
        `${API_URL}/api/contabilidad/retenciones/${retencionId}/pdf`,
        {
          headers: getAuthHeaders(),
          responseType: "blob"
        }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `Retencion_${retencionId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      showSnackbar("PDF de retención descargado", "success");
    } catch (error) {
      console.error("Error descargando PDF:", error);
      showSnackbar("Error al descargar PDF de retención", "error");
    }
  };

  const enviarRetencionSRI = async (retencionId) => {
    if (!retencionId) {
      showSnackbar("No se encontró el ID de la retención", "error");
      return;
    }
    try {
      setLoading(true);
      showSnackbar("Enviando retención al SRI...", "info");
      const response = await axios.post(
        `${API_URL}/api/retenciones-sri/${retencionId}/enviar-sri`,
        {},
        { headers: getAuthHeaders() }
      );
      if (response.data.success) {
        showSnackbar(`Retención enviada al SRI. Estado: ${response.data.estado}. ${response.data.message || ""}`, response.data.estado === "RECHAZADO" ? "warning" : "success");
        cargarComprasSinRetencion();
      } else {
        showSnackbar(response.data.error || "Error al enviar al SRI", "error");
      }
    } catch (error) {
      console.error("Error enviando retención al SRI:", error);
      showSnackbar(error.response?.data?.error || "Error al enviar retención al SRI", "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Receipt sx={{ fontSize: 32 }} />
          <Typography variant="h4">Retenciones</Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={cargarComprasSinRetencion}
          >
            Actualizar
          </Button>
        </Box>
      </Box>

      {/* Tabla de Compras sin Retención */}
      <Card>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                <TableCell>Fecha</TableCell>
                <TableCell>Proveedor</TableCell>
                <TableCell>RUC</TableCell>
                <TableCell>Factura</TableCell>
                <TableCell align="right">Autorización Factura</TableCell>
                <TableCell align="right">Total</TableCell>
                <TableCell align="center">Retenciones</TableCell>
                <TableCell align="center">Autorización Retención</TableCell>
                <TableCell align="center">A Pagar</TableCell>
                <TableCell align="center">Documentos PDF</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={10} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : compras.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} align="center">
                    <Box sx={{ py: 4 }}>
                      <Typography color="textSecondary" gutterBottom>
                        No hay compras registradas
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Primero debes registrar una compra en el módulo de Compras
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                compras.map((compra) => (
                  <TableRow key={compra.id} hover>
                    <TableCell>{formatDate(compra.fecha_emision)}</TableCell>
                    <TableCell>{compra.proveedor?.razonSocial || "N/A"}</TableCell>
                    <TableCell>{compra.proveedor?.ruc || "N/A"}</TableCell>
                    <TableCell>{compra.factura?.numeroComprobante || "N/A"}</TableCell>
                    <TableCell align="right">
                      <Chip
                        label="AUTORIZADA"
                        color="success"
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="right">
                      {formatCurrency(compra.totales?.importeTotal)}
                    </TableCell>
                    <TableCell align="center">
                      {compra.tiene_retencion ? (
                        <Chip
                          label="CON RETENCIÓN"
                          color="success"
                          size="small"
                        />
                      ) : (
                        formatCurrency(0)
                      )}
                    </TableCell>
                    <TableCell align="center">
                      {compra.tiene_retencion ? (
                        compra.retencion?.estado_sri === "AUTORIZADO" ? (
                          <Chip label="AUTORIZADA SRI" color="success" size="small" />
                        ) : compra.retencion?.estado_sri === "PENDIENTE" ? (
                          <Chip label="PENDIENTE SRI" color="warning" size="small" />
                        ) : compra.retencion?.estado_sri === "RECHAZADO" ? (
                          <Chip label="RECHAZADA SRI" color="error" size="small" />
                        ) : (
                          <Chip label="SIN ENVIAR" color="default" size="small" variant="outlined" />
                        )
                      ) : (
                        <Chip label="N/A" size="small" variant="outlined" />
                      )}
                    </TableCell>
                    <TableCell align="center">
                      {formatCurrency(compra.totales?.importeTotal)}
                    </TableCell>
                    <TableCell align="center">
                      {compra.tiene_retencion ? (
                        <Box sx={{ display: "flex", gap: 1, justifyContent: "center" }}>
                          <Tooltip title="Descargar PDF Retención">
                            <IconButton
                              color="secondary"
                              onClick={() => descargarPDFRetencion(compra.retencion_id)}
                            >
                              <PictureAsPdf />
                            </IconButton>
                          </Tooltip>
                          {(!compra.retencion?.estado_sri || compra.retencion?.estado_sri !== "AUTORIZADO") && (
                            <Tooltip title="Enviar al SRI (Firmar y Autorizar)">
                              <IconButton
                                color="primary"
                                onClick={() => enviarRetencionSRI(compra.retencion_id)}
                                disabled={loading}
                              >
                                <GetApp />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      ) : (
                        <Tooltip title="Crear Retención">
                          <IconButton
                            color="primary"
                            onClick={() => abrirDialogCreacion(compra)}
                          >
                            <Description />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Dialog de Creación de Retención */}
      <Dialog
        open={dialogOpen}
        onClose={cerrarDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography variant="h6">Nueva Retención</Typography>
            <IconButton onClick={cerrarDialog}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        
        <DialogContent>
          <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {/* Paso 0: Datos de la Compra */}
          {activeStep === 0 && compraSeleccionada && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Datos de la Compra
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Proveedor"
                    value={compraSeleccionada.proveedor?.razonSocial || ""}
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="RUC"
                    value={compraSeleccionada.proveedor?.ruc || ""}
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Factura"
                    value={compraSeleccionada.factura?.numeroComprobante || ""}
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Fecha Emisión"
                    value={formatDate(compraSeleccionada.fecha_emision)}
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Subtotal"
                    value={formatCurrency(compraSeleccionada.totales?.subtotal15 || 0)}
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="IVA"
                    value={formatCurrency(compraSeleccionada.totales?.iva15 || 0)}
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Total"
                    value={formatCurrency(compraSeleccionada.totales?.importeTotal || 0)}
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
              </Grid>
            </Box>
          )}

          {/* Paso 1: Configurar Retención */}
          {activeStep === 1 && (
            <Box>
              {/* Retención en la Fuente */}
              <Paper sx={{ p: 2, mb: 2 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={retencionData.aplicarRetencionRenta}
                      onChange={(e) =>
                        setRetencionData({
                          ...retencionData,
                          aplicarRetencionRenta: e.target.checked
                        })
                      }
                    />
                  }
                  label={<Typography variant="h6">Retención en la Fuente</Typography>}
                />
                
                {retencionData.aplicarRetencionRenta && (
                  <Box sx={{ mt: 2 }}>
                    <FormControl fullWidth sx={{ mb: 2 }}>
                      <InputLabel>Código de Retención en la Fuente</InputLabel>
                      <Select
                        value={retencionData.codigoRetencionRenta}
                        label="Código de Retención en la Fuente"
                        onChange={(e) =>
                          handleChangeRetencionRenta("codigoRetencionRenta", e.target.value)
                        }
                      >
                        {catalogosSRI.retencionesRenta.length > 0 ? (
                          catalogosSRI.retencionesRenta.map((ret) => (
                            <MenuItem key={ret.value || ret.codigo} value={ret.value || ret.codigo}>
                              {ret.label || (ret.codigo + " - " + ret.descripcion + " (" + ret.porcentaje + "%)")}
                            </MenuItem>
                          ))
                        ) : (
                          <>
                            <MenuItem value="303">303 - Honorarios, comisiones (10%)</MenuItem>
                            <MenuItem value="304">304 - Servicios predomina mano de obra (2%)</MenuItem>
                            <MenuItem value="307">307 - Servicios entre sociedades (2%)</MenuItem>
                            <MenuItem value="312">312 - Transferencia bienes muebles (1%)</MenuItem>
                            <MenuItem value="332">332 - Otras compras bienes (1%)</MenuItem>
                            <MenuItem value="344">344 - Aplicables 8% (8%)</MenuItem>
                          </>
                        )}
                      </Select>
                    </FormControl>
                    <Grid container spacing={2}>
                      <Grid item xs={4}>
                        <TextField
                          fullWidth
                          label="Base Imponible"
                          type="number"
                          value={retencionData.baseImponibleRenta}
                          onChange={(e) =>
                            handleChangeRetencionRenta("baseImponibleRenta", parseFloat(e.target.value))
                          }
                        />
                      </Grid>
                      <Grid item xs={4}>
                        <TextField
                          fullWidth
                          label="Porcentaje"
                          value={`${retencionData.porcentajeRetencionRenta}%`}
                          InputProps={{ readOnly: true }}
                        />
                      </Grid>
                      <Grid item xs={4}>
                        <TextField
                          fullWidth
                          label="Valor Retenido"
                          value={formatCurrency(retencionData.valorRetencionRenta)}
                          InputProps={{ readOnly: true }}
                        />
                      </Grid>
                    </Grid>
                  </Box>
                )}
              </Paper>

              {/* Retención de IVA */}
              <Paper sx={{ p: 2 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={retencionData.aplicarRetencionIva}
                      onChange={(e) =>
                        setRetencionData({
                          ...retencionData,
                          aplicarRetencionIva: e.target.checked
                        })
                      }
                    />
                  }
                  label={<Typography variant="h6">Retención de IVA</Typography>}
                />
                
                {retencionData.aplicarRetencionIva && (
                  <Box sx={{ mt: 2 }}>
                    <FormControl fullWidth sx={{ mb: 2 }}>
                      <InputLabel>Código de Retención de IVA</InputLabel>
                      <Select
                        value={retencionData.codigoRetencionIva}
                        label="Código de Retención de IVA"
                        onChange={(e) =>
                          handleChangeRetencionIva("codigoRetencionIva", e.target.value)
                        }
                      >
                        {catalogosSRI.retencionesIVA.length > 0 ? (
                          catalogosSRI.retencionesIVA.map((ret) => (
                            <MenuItem key={ret.value || ret.codigo} value={ret.value || ret.codigo}>
                              {ret.label || `${ret.value || ret.codigo} - ${ret.descripcion} (${ret.porcentaje}%)`}
                            </MenuItem>
                          ))
                        ) : (
                          <>
                            <MenuItem value="1">1 - Retención 10% IVA</MenuItem>
                            <MenuItem value="2">2 - Retención 20% IVA</MenuItem>
                            <MenuItem value="3">3 - Retención 30% IVA</MenuItem>
                            <MenuItem value="9">9 - Retención 70% IVA</MenuItem>
                            <MenuItem value="10">10 - Retención 100% IVA</MenuItem>
                          </>
                        )}
                      </Select>
                    </FormControl>
                    <Grid container spacing={2}>
                      <Grid item xs={4}>
                        <TextField
                          fullWidth
                          label="Base Imponible"
                        type="number"
                        value={retencionData.baseImponibleIva}
                        onChange={(e) =>
                          handleChangeRetencionIva("baseImponibleIva", parseFloat(e.target.value))
                        }
                      />
                    </Grid>
                    <Grid item xs={4}>
                      <TextField
                        fullWidth
                        label="Porcentaje"
                        value={`${retencionData.porcentajeRetencionIva}%`}
                        InputProps={{ readOnly: true }}
                      />
                    </Grid>
                    <Grid item xs={4}>
                      <TextField
                        fullWidth
                        label="Valor Retenido"
                        value={formatCurrency(retencionData.valorRetencionIva)}
                        InputProps={{ readOnly: true }}
                      />
                    </Grid>
                    </Grid>
                  </Box>
                )}
              </Paper>
            </Box>
          )}

          {/* Paso 2: Confirmar */}
          {activeStep === 2 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Resumen de la Retención
              </Typography>
              
              <Paper sx={{ p: 2, mb: 2 }}>
                <Typography variant="subtitle2" color="textSecondary">
                  Comprobante de Retención
                </Typography>
                <Typography variant="h6">
                  {retencionData.establecimiento}-{retencionData.puntoEmision}-{retencionData.secuencial}
                </Typography>
              </Paper>

              {retencionData.aplicarRetencionRenta && (
                <Paper sx={{ p: 2, mb: 2 }}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Retención en la Fuente
                  </Typography>
                  <Typography>
                    Base: {formatCurrency(retencionData.baseImponibleRenta)} × {retencionData.porcentajeRetencionRenta}%
                  </Typography>
                  <Typography variant="h6" color="primary">
                    {formatCurrency(retencionData.valorRetencionRenta)}
                  </Typography>
                </Paper>
              )}

              {retencionData.aplicarRetencionIva && (
                <Paper sx={{ p: 2, mb: 2 }}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Retención de IVA
                  </Typography>
                  <Typography>
                    Base: {formatCurrency(retencionData.baseImponibleIva)} × {retencionData.porcentajeRetencionIva}%
                  </Typography>
                  <Typography variant="h6" color="primary">
                    {formatCurrency(retencionData.valorRetencionIva)}
                  </Typography>
                </Paper>
              )}

              <Paper sx={{ p: 2, backgroundColor: "#f5f5f5" }}>
                <Typography variant="subtitle2" color="textSecondary">
                  Total Retenido
                </Typography>
                <Typography variant="h5" color="error">
                  {formatCurrency(totalRetenido())}
                </Typography>
              </Paper>
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={cerrarDialog}>Cancelar</Button>
          {activeStep > 0 && (
            <Button onClick={handleBack}>Atrás</Button>
          )}
          {activeStep < steps.length - 1 ? (
            <Button variant="contained" onClick={handleNext}>
              Siguiente
            </Button>
          ) : (
            <Button
              variant="contained"
              color="success"
              onClick={crearRetencion}
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} /> : "Generar Retención"}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      {snackbar.open && (
        <Alert
          severity={snackbar.severity}
          onClose={handleCloseSnackbar}
          sx={{ position: "fixed", bottom: 20, right: 20, zIndex: 9999 }}
        >
          {snackbar.message}
        </Alert>
      )}
    </Box>
  );
}
